"use client"

import { DocumentClassifier } from "@/types"
import { createContext, useContext, useState, type ReactNode } from "react"

interface SchemaHistoryEntry {
  timestamp: number
  schema: any
  label?: string
}

interface VisualizerContextType {
  dataSchema: any
  setDataSchema: (schema: any) => void
  visualizerConfig: any
  setVisualizerConfig: (config: any) => void
  data: any
  setData: (data: any) => void
  selectedElement: number[] | null
  setSelectedElement: (path: number[] | null) => void
  moveElement: (fromPath: number[], toIndex: number, tabIndex: number) => void
  reorderKeys: (tabIndex: number, componentPath: number[], fromIndex: number, toIndex: number) => void
  reorderField: (
    tabIndex: number,
    componentPath: number[],
    fromRow: number,
    fromCol: number,
    toRow: number,
    toCol: number,
  ) => void
  removeField: (tabIndex: number, componentPath: number[], rowIndex: number, colIndex: number) => void
  deleteTab: (tabIndex: number) => void
  schemaHistory: SchemaHistoryEntry[]
  addSchemaToHistory: (schema: any, label?: string) => void
  loadSchemaFromHistory: (index: number) => void
}

const VisualizerContext = createContext<VisualizerContextType | undefined>(undefined)

type VisualizerConfig = {
  config: DocumentClassifier
  data?: any
  children: ReactNode
}

export function VisualizerProvider({ config, children, data: initialData }: VisualizerConfig) {
  if (!config || !config.visualizer_json || !config.extraction_schema) {
	  return <VisualizerContext.Provider
	  value={{
		dataSchema: {},
		setDataSchema: () => {},
		visualizerConfig: {},
		setVisualizerConfig: () => {},
		data: null,
		setData: () => {},
		selectedElement: null,
		setSelectedElement: () => {},
		moveElement: () => {},
		reorderKeys: () => {},
		reorderField: () => {},
		removeField: () => {},
		deleteTab: () => {},
		schemaHistory: [],
		addSchemaToHistory: () => {},
		loadSchemaFromHistory: () => {},
			  }}
	>
	  {children}
	</VisualizerContext.Provider>

  }

  // Initial states (allow `initialData` to be null)
  const [visualizerConfig, setVisualizerConfig] = useState<any>(config.visualizer_json)
  const [dataSchema, setDataSchema] = useState<any>(config.extraction_schema?.schema || {})
  const [data, setData] = useState<any>(initialData ?? null)

  console.log("!!!!!! VisualizerProvider initial data:", initialData)
  const [selectedElement, setSelectedElement] = useState<number[] | null>(null)
  const [schemaHistory, setSchemaHistory] = useState<SchemaHistoryEntry[]>([
    {
      timestamp: Date.now(),
      schema: JSON.parse(JSON.stringify(config.extraction_schema?.schema || {})),
      label: "Initial Schema",
    },
  ])

  const moveElement = (fromPath: number[], toIndex: number, tabIndex: number) => {
    console.log("[v0] moveElement called - fromPath:", fromPath, "toIndex:", toIndex, "tabIndex:", tabIndex)

    setVisualizerConfig((prev: any) => {
      const newConfig = JSON.parse(JSON.stringify(prev))
      const tab = newConfig.tabs?.[tabIndex]

      if (!tab || !tab.layout) {
        console.log("[v0] moveElement - tab or layout not found")
        return prev
      }

      if (fromPath.length === 1) {
        const fromIndex = fromPath[0]
        console.log("[v0] Moving top-level item from index", fromIndex, "to index", toIndex)

        const [movedItem] = tab.layout.splice(fromIndex, 1)
        console.log("[v0] Removed item:", movedItem)

        const adjustedToIndex = toIndex > fromIndex ? toIndex - 1 : toIndex
        console.log("[v0] Adjusted toIndex:", adjustedToIndex)

        tab.layout.splice(adjustedToIndex, 0, movedItem)
        console.log(
          "[v0] New layout order:",
          tab.layout.map((item: any) => item.header || item.type),
        )

        return newConfig
      }

      console.log("[v0] Nested reordering not yet implemented")
      return prev
    })
  }

  const reorderKeys = (tabIndex: number, componentPath: number[], fromIndex: number, toIndex: number) => {
    setVisualizerConfig((prev: any) => {
      const newConfig = JSON.parse(JSON.stringify(prev))
      const tab = newConfig.tabs?.[tabIndex]

      if (!tab || !tab.layout) return prev

      // Navigate to the component using the path
      let component = tab.layout[componentPath[0]]
      for (let i = 1; i < componentPath.length; i++) {
        if (component?.children) {
          component = component.children[componentPath[i]]
        }
      }

      if (!component || !component.keys) return prev

      // Reorder the keys array
      const [movedKey] = component.keys.splice(fromIndex, 1)
      const adjustedToIndex = toIndex > fromIndex ? toIndex - 1 : toIndex
      component.keys.splice(adjustedToIndex, 0, movedKey)

      return newConfig
    })
  }

  const reorderField = (
    tabIndex: number,
    componentPath: number[],
    fromRow: number,
    fromCol: number,
    toRow: number,
    toCol: number,
  ) => {
    setVisualizerConfig((prev: any) => {
      const newConfig = JSON.parse(JSON.stringify(prev))
      const tab = newConfig.tabs?.[tabIndex]

      if (!tab || !tab.layout) return prev

      // Navigate to the component using the path
      let component = tab.layout[componentPath[0]]
      for (let i = 1; i < componentPath.length; i++) {
        if (component?.children) {
          component = component.children[componentPath[i]]
        }
      }

      if (!component || !component.keys) return prev

      // Get the source row and field
      const sourceRow = component.keys[fromRow]
      const sourceFields = Array.isArray(sourceRow) ? sourceRow : [sourceRow]
      const movedField = sourceFields[fromCol]

      // Remove field from source row
      if (Array.isArray(sourceRow)) {
        sourceRow.splice(fromCol, 1)
        // If row is now empty, remove it
        if (sourceRow.length === 0) {
          component.keys.splice(fromRow, 1)
          // Adjust toRow if needed
          if (toRow > fromRow) {
            toRow--
          }
        } else if (sourceRow.length === 1) {
          // Convert single-item array to string
          component.keys[fromRow] = sourceRow[0]
        }
      } else {
        // Single field row, remove it
        component.keys.splice(fromRow, 1)
        // Adjust toRow if needed
        if (toRow > fromRow) {
          toRow--
        }
      }

      // Insert field into target row
      if (toRow >= component.keys.length) {
        // Create new row at the end
        component.keys.push(movedField)
      } else {
        const targetRow = component.keys[toRow]
        if (Array.isArray(targetRow)) {
          // Insert into existing array row
          targetRow.splice(toCol, 0, movedField)
        } else {
          // Convert single field to array and insert
          const newRow = [targetRow]
          newRow.splice(toCol, 0, movedField)
          component.keys[toRow] = newRow
        }
      }

      return newConfig
    })
  }

  const deleteTab = (tabIndex: number) => {
    setVisualizerConfig((prev: any) => {
      const newConfig = JSON.parse(JSON.stringify(prev))

      // Don't allow deleting the last tab
      if (!newConfig.tabs || newConfig.tabs.length <= 1) {
        console.log("[v0] Cannot delete the last tab")
        return prev
      }

      newConfig.tabs.splice(tabIndex, 1)
      console.log("[v0] Deleted tab at index", tabIndex)

      return newConfig
    })
  }

  const addSchemaToHistory = (schema: any, label?: string) => {
    setSchemaHistory((prev) => [
      ...prev,
      {
        timestamp: Date.now(),
        schema: JSON.parse(JSON.stringify(schema)),
        label,
      },
    ])
  }

  const loadSchemaFromHistory = (index: number) => {
    const entry = schemaHistory[index]
    if (entry) {
      setDataSchema(JSON.parse(JSON.stringify(entry.schema)))
      console.log("[v0] Loaded schema from history:", entry.label || new Date(entry.timestamp).toLocaleString())
    }
  }

  const removeField = (tabIndex: number, componentPath: number[], rowIndex: number, colIndex: number) => {
    setVisualizerConfig((prev: any) => {
      const newConfig = JSON.parse(JSON.stringify(prev))
      const tab = newConfig.tabs?.[tabIndex]

      if (!tab || !tab.layout) return prev

      // Navigate to the component using the path
      let component = tab.layout[componentPath[0]]
      for (let i = 1; i < componentPath.length; i++) {
        if (component?.children) {
          component = component.children[componentPath[i]]
        }
      }

      if (!component || !component.keys) return prev

      const row = component.keys[rowIndex]

      if (Array.isArray(row)) {
        // Remove field from array row
        row.splice(colIndex, 1)

        // If row is now empty, remove it
        if (row.length === 0) {
          component.keys.splice(rowIndex, 1)
        } else if (row.length === 1) {
          // Convert single-item array to string
          component.keys[rowIndex] = row[0]
        }
      } else {
        // Single field row, remove the entire row
        component.keys.splice(rowIndex, 1)
      }

      return newConfig
    })
  }

  return (
    <VisualizerContext.Provider
      value={{
        dataSchema,
        setDataSchema,
        visualizerConfig,
        setVisualizerConfig,
        data,
        setData,
        selectedElement,
        setSelectedElement,
        moveElement,
        reorderKeys,
        reorderField,
        removeField,
        deleteTab,
        schemaHistory,
        addSchemaToHistory,
        loadSchemaFromHistory,
      }}
    >
      {children}
    </VisualizerContext.Provider>
  )
}

export function useVisualizer() {
  const context = useContext(VisualizerContext)
  if (context === undefined) {
    throw new Error("useVisualizer must be used within a VisualizerProvider")
  }
  return context
}



//"use client"
//
//import { DocumentClassifier } from "@/types"
//import { createContext, useContext, useState, type ReactNode } from "react"
//
//
//
//interface SchemaHistoryEntry {
//  timestamp: number
//  schema: any
//  label?: string
//}
//
//interface VisualizerContextType {
//  dataSchema: any
//  setDataSchema: (schema: any) => void
//  visualizerConfig: any
//  setVisualizerConfig: (config: any) => void
//  data: any
//  setData: (data: any) => void
//  selectedElement: number[] | null
//  setSelectedElement: (path: number[] | null) => void
//  moveElement: (fromPath: number[], toIndex: number, tabIndex: number) => void
//  reorderKeys: (tabIndex: number, componentPath: number[], fromIndex: number, toIndex: number) => void
//  reorderField: (
//    tabIndex: number,
//    componentPath: number[],
//    fromRow: number,
//    fromCol: number,
//    toRow: number,
//    toCol: number,
//  ) => void
//  removeField: (tabIndex: number, componentPath: number[], rowIndex: number, colIndex: number) => void
//  deleteTab: (tabIndex: number) => void
//  schemaHistory: SchemaHistoryEntry[]
//  addSchemaToHistory: (schema: any, label?: string) => void
//  loadSchemaFromHistory: (index: number) => void
//}
//
//const VisualizerContext = createContext<VisualizerContextType | undefined>(undefined)
//
//type VisualizerConfig = {
//	config: DocumentClassifier
//	data?: any
//	children: ReactNode
//}
//
//
//export function VisualizerProvider({ config, children, data:intitialData }: VisualizerConfig) {
//
//	if (config === null || !config.visualizer_json || intitialData || !config.extraction_schema) {
//		console.log("!!!!!! VisualizerProvider missing config, visualizer_json, data, or extraction_schema")
//		return
//	}
//
//	const [visualizerConfig, setVisualizerConfig] = useState<DocumentClassifier["visualizer_json"]>(config.visualizer_json)
//	const [dataSchema, setDataSchema] = useState<DocumentClassifier["extraction_schema"]>(config.extraction_schema?.schema || {})
//
//
//
//  //const [dataSchema, setDataSchema] = useState()
//  //const [visualizerConfig, setVisualizerConfig] = useState(defaultVisualizerConfig)
//  const [data, setData] = useState(intitialData )
//  console.log("!!!!!! VisualizerProvider initial data:", intitialData)
//  const [selectedElement, setSelectedElement] = useState<number[] | null>(null)
//  const [schemaHistory, setSchemaHistory] = useState<SchemaHistoryEntry[]>([
//    {
//      timestamp: Date.now(),
//      schema: {},
//      label: "Initial Schema",
//    },
//  ])
//
//  const moveElement = (fromPath: number[], toIndex: number, tabIndex: number) => {
//    console.log("[v0] moveElement called - fromPath:", fromPath, "toIndex:", toIndex, "tabIndex:", tabIndex)
//
//    setVisualizerConfig((prev: any) => {
//      const newConfig = JSON.parse(JSON.stringify(prev))
//      const tab = newConfig.tabs[tabIndex]
//
//      if (!tab || !tab.layout) {
//        console.log("[v0] moveElement - tab or layout not found")
//        return prev
//      }
//
//      if (fromPath.length === 1) {
//        const fromIndex = fromPath[0]
//        console.log("[v0] Moving top-level item from index", fromIndex, "to index", toIndex)
//
//        const [movedItem] = tab.layout.splice(fromIndex, 1)
//        console.log("[v0] Removed item:", movedItem)
//
//        const adjustedToIndex = toIndex > fromIndex ? toIndex - 1 : toIndex
//        console.log("[v0] Adjusted toIndex:", adjustedToIndex)
//
//        tab.layout.splice(adjustedToIndex, 0, movedItem)
//        console.log(
//          "[v0] New layout order:",
//          tab.layout.map((item: any) => item.header || item.type),
//        )
//
//        return newConfig
//      }
//
//      console.log("[v0] Nested reordering not yet implemented")
//      return prev
//    })
//  }
//
//  const reorderKeys = (tabIndex: number, componentPath: number[], fromIndex: number, toIndex: number) => {
//    setVisualizerConfig((prev: any) => {
//      const newConfig = JSON.parse(JSON.stringify(prev))
//      const tab = newConfig.tabs[tabIndex]
//
//      if (!tab || !tab.layout) return prev
//
//      // Navigate to the component using the path
//      let component = tab.layout[componentPath[0]]
//      for (let i = 1; i < componentPath.length; i++) {
//        if (component.children) {
//          component = component.children[componentPath[i]]
//        }
//      }
//
//      if (!component || !component.keys) return prev
//
//      // Reorder the keys array
//      const [movedKey] = component.keys.splice(fromIndex, 1)
//      const adjustedToIndex = toIndex > fromIndex ? toIndex - 1 : toIndex
//      component.keys.splice(adjustedToIndex, 0, movedKey)
//
//      return newConfig
//    })
//  }
//
//  const reorderField = (
//    tabIndex: number,
//    componentPath: number[],
//    fromRow: number,
//    fromCol: number,
//    toRow: number,
//    toCol: number,
//  ) => {
//    setVisualizerConfig((prev: any) => {
//      const newConfig = JSON.parse(JSON.stringify(prev))
//      const tab = newConfig.tabs[tabIndex]
//
//      if (!tab || !tab.layout) return prev
//
//      // Navigate to the component using the path
//      let component = tab.layout[componentPath[0]]
//      for (let i = 1; i < componentPath.length; i++) {
//        if (component.children) {
//          component = component.children[componentPath[i]]
//        }
//      }
//
//      if (!component || !component.keys) return prev
//
//      // Get the source row and field
//      const sourceRow = component.keys[fromRow]
//      const sourceFields = Array.isArray(sourceRow) ? sourceRow : [sourceRow]
//      const movedField = sourceFields[fromCol]
//
//      // Remove field from source row
//      if (Array.isArray(sourceRow)) {
//        sourceRow.splice(fromCol, 1)
//        // If row is now empty, remove it
//        if (sourceRow.length === 0) {
//          component.keys.splice(fromRow, 1)
//          // Adjust toRow if needed
//          if (toRow > fromRow) {
//            toRow--
//          }
//        } else if (sourceRow.length === 1) {
//          // Convert single-item array to string
//          component.keys[fromRow] = sourceRow[0]
//        }
//      } else {
//        // Single field row, remove it
//        component.keys.splice(fromRow, 1)
//        // Adjust toRow if needed
//        if (toRow > fromRow) {
//          toRow--
//        }
//      }
//
//      // Insert field into target row
//      if (toRow >= component.keys.length) {
//        // Create new row at the end
//        component.keys.push(movedField)
//      } else {
//        const targetRow = component.keys[toRow]
//        if (Array.isArray(targetRow)) {
//          // Insert into existing array row
//          targetRow.splice(toCol, 0, movedField)
//        } else {
//          // Convert single field to array and insert
//          const newRow = [targetRow]
//          newRow.splice(toCol, 0, movedField)
//          component.keys[toRow] = newRow
//        }
//      }
//
//      return newConfig
//    })
//  }
//
//  const deleteTab = (tabIndex: number) => {
//    setVisualizerConfig((prev: any) => {
//      const newConfig = JSON.parse(JSON.stringify(prev))
//
//      // Don't allow deleting the last tab
//      if (newConfig.tabs.length <= 1) {
//        console.log("[v0] Cannot delete the last tab")
//        return prev
//      }
//
//      newConfig.tabs.splice(tabIndex, 1)
//      console.log("[v0] Deleted tab at index", tabIndex)
//
//      return newConfig
//    })
//  }
//
//  const addSchemaToHistory = (schema: any, label?: string) => {
//    setSchemaHistory((prev) => [
//      ...prev,
//      {
//        timestamp: Date.now(),
//        schema: JSON.parse(JSON.stringify(schema)),
//        label,
//      },
//    ])
//  }
//
//  const loadSchemaFromHistory = (index: number) => {
//    const entry = schemaHistory[index]
//    if (entry) {
//      setDataSchema(JSON.parse(JSON.stringify(entry.schema)))
//      console.log("[v0] Loaded schema from history:", entry.label || new Date(entry.timestamp).toLocaleString())
//    }
//  }
//
//  const removeField = (tabIndex: number, componentPath: number[], rowIndex: number, colIndex: number) => {
//    setVisualizerConfig((prev: any) => {
//      const newConfig = JSON.parse(JSON.stringify(prev))
//      const tab = newConfig.tabs[tabIndex]
//
//      if (!tab || !tab.layout) return prev
//
//      // Navigate to the component using the path
//      let component = tab.layout[componentPath[0]]
//      for (let i = 1; i < componentPath.length; i++) {
//        if (component.children) {
//          component = component.children[componentPath[i]]
//        }
//      }
//
//      if (!component || !component.keys) return prev
//
//      const row = component.keys[rowIndex]
//
//      if (Array.isArray(row)) {
//        // Remove field from array row
//        row.splice(colIndex, 1)
//
//        // If row is now empty, remove it
//        if (row.length === 0) {
//          component.keys.splice(rowIndex, 1)
//        } else if (row.length === 1) {
//          // Convert single-item array to string
//          component.keys[rowIndex] = row[0]
//        }
//      } else {
//        // Single field row, remove the entire row
//        component.keys.splice(rowIndex, 1)
//      }
//
//      return newConfig
//    })
//  }
//
//  return (
//    <VisualizerContext.Provider
//      value={{
//        dataSchema,
//        setDataSchema,
//        visualizerConfig,
//        setVisualizerConfig,
//        data,
//        setData,
//        selectedElement,
//        setSelectedElement,
//        moveElement,
//        reorderKeys,
//        reorderField,
//        removeField,
//        deleteTab,
//        schemaHistory,
//        addSchemaToHistory,
//        loadSchemaFromHistory,
//      }}
//    >
//      {children}
//    </VisualizerContext.Provider>
//  )
//}
//
//export function useVisualizer() {
//  const context = useContext(VisualizerContext)
//  if (context === undefined) {
//    throw new Error("useVisualizer must be used within a VisualizerProvider")
//  }
//  return context
//}
//
